import React, { useState } from 'react';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon
} from 'react-share';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface ShareMenuProps {
  /** URL to share */
  url: string;
  /** Title or quote for share buttons */
  title?: string;
  /** Optional custom trigger element (e.g. a Button) */
  children?: React.ReactNode;
}

/**
 * ShareMenu: generic share menu with social buttons and copy link
 */
const ShareMenu: React.FC<ShareMenuProps> = ({ url, title, children }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    handleClose();
  };

  // Determine trigger element: use children or default IconButton
  const trigger = children
    ? React.cloneElement(children as React.ReactElement<any>, { onClick: handleOpen })
    : (
      <IconButton onClick={handleOpen} aria-label="Share">
        <ShareIcon />
      </IconButton>
    );

  return (
    <>
      {trigger}
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // Match menu min width to trigger button width
        PaperProps={{
          style: {
            minWidth: anchorEl ? anchorEl.getBoundingClientRect().width : undefined,
          },
        }}
      >
        {/* Facebook */}
        <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
          <FacebookShareButton
            url={url}
            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
          >
            <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
              <FacebookIcon size={40} round />
            </ListItemIcon>
            <ListItemText sx={{ textAlign: 'left' }}>Facebook</ListItemText>
          </FacebookShareButton>
        </MenuItem>
        {/* X / Twitter */}
        <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
          <TwitterShareButton
            url={url}
            title={title}
            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
          >
            <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
              <TwitterIcon size={40} round />
            </ListItemIcon>
            <ListItemText sx={{ textAlign: 'left' }}>X</ListItemText>
          </TwitterShareButton>
        </MenuItem>
        {/* LinkedIn */}
        <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
          <LinkedinShareButton
            url={url}
            title={title}
            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
          >
            <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
              <LinkedinIcon size={40} round />
            </ListItemIcon>
            <ListItemText sx={{ textAlign: 'left' }}>LinkedIn</ListItemText>
          </LinkedinShareButton>
        </MenuItem>
        {/* Copy link */}
        <MenuItem onClick={handleCopy} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
            <IconButton size="small">
              <ContentCopyIcon />
            </IconButton>
          </ListItemIcon>
          <ListItemText sx={{ textAlign: 'left' }}>Copy Link</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ShareMenu;